import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import type { SalesOrderStatus } from '@/lib/api/sales-order-api'
import type {
    SLAConfig,
    SLARegion,
    UpsertSLAConfigRequest,
} from '@/lib/api/sla-config-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SLAConfigAddDialog } from '@/components/settings/sla-config-add-dialog'
import { STATUS_LABELS } from '@/components/sections/order-status-badge'
import {
    useSLAConfigs,
    useSetSLAConfigActive,
    useUpsertSLAConfig,
} from '@/hooks/use-sla-config-query'
import { useSLAConfigTracking } from '@/hooks/use-sla-config-tracking'
import { slaRegionLabel } from '@/lib/sla-region'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/settings/sla')({
    component: SLASettingsPage,
})

interface EditRow {
    status: string
    // Sửa một dòng phải gửi lại đúng nhà vận chuyển của dòng đó, nếu không
    // upsert sẽ ghi đè dòng mặc định cùng trạng thái.
    shipping_carrier_id: string | null
    region: SLARegion | null
    duration_minutes: number
    warning_minutes: number | ''
}

// Trạng thái lạ (backend thêm mới) vẫn hiện nguyên văn thay vì vỡ giao diện.
function statusLabel(status: string): string {
    const label = STATUS_LABELS[status as SalesOrderStatus]
    return label ? label() : status
}

function SLASettingsPage() {
    const { data: configs = [], isLoading } = useSLAConfigs()
    const upsertMutation = useUpsertSLAConfig()
    const activeMutation = useSetSLAConfigActive()
    const track = useSLAConfigTracking()

    const [editing, setEditing] = useState<EditRow | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [adding, setAdding] = useState(false)

    const startEdit = (cfg: SLAConfig) => {
        setEditingId(cfg.id)
        setEditing({
            status: cfg.status,
            shipping_carrier_id: cfg.shipping_carrier_id ?? null,
            region: cfg.region ?? null,
            duration_minutes: cfg.duration_minutes,
            warning_minutes: cfg.warning_minutes ?? '',
        })
    }

    const handleSave = async () => {
        if (!editing) return
        const scope = editing.shipping_carrier_id ? 'carrier' : 'default'
        try {
            await upsertMutation.mutateAsync({
                status: editing.status,
                shipping_carrier_id: editing.shipping_carrier_id,
                region: editing.region,
                duration_minutes: editing.duration_minutes,
                warning_minutes:
                    editing.warning_minutes !== ''
                        ? editing.warning_minutes
                        : undefined,
            })
        } catch {
            // Toast lỗi do mutation lo; ở đây chỉ đếm lượt lưu hỏng.
            track({ scope, mode: 'updated', result: 'error' })
            return
        }
        track({ scope, mode: 'updated', result: 'saved' })
        setEditing(null)
        setEditingId(null)
    }

    const handleAdd = async (data: UpsertSLAConfigRequest) => {
        const scope = data.shipping_carrier_id ? 'carrier' : 'default'
        try {
            await upsertMutation.mutateAsync(data)
        } catch (err) {
            track({ scope, mode: 'created', result: 'error' })
            throw err
        }
        track({ scope, mode: 'created', result: 'saved' })
    }

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-3xl space-y-6 page-pad">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div>
                    <h1 className="text-xl font-semibold">
                        {m.sla_settings_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {m.sla_settings_description()}
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                    <CardTitle>{m.sla_settings_title()}</CardTitle>
                    <Button size="sm" onClick={() => setAdding(true)}>
                        {m.sla_add()}
                    </Button>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{m.sla_status()}</TableHead>
                                <TableHead>{m.sla_carrier()}</TableHead>
                                <TableHead>{m.sla_region()}</TableHead>
                                <TableHead>{m.sla_duration()}</TableHead>
                                <TableHead>{m.sla_warning()}</TableHead>
                                <TableHead>{m.sla_active()}</TableHead>
                                <TableHead>{m.common_actions()}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {configs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className="text-center text-muted-foreground py-6"
                                    >
                                        {m.common_no_data()}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                configs.map((cfg) => (
                                    <TableRow key={cfg.id}>
                                        <TableCell className="font-medium">
                                            {statusLabel(cfg.status)}
                                        </TableCell>
                                        <TableCell
                                            className={
                                                cfg.shipping_carrier_id
                                                    ? ''
                                                    : 'text-muted-foreground'
                                            }
                                        >
                                            {cfg.shipping_carrier_name ||
                                                m.sla_carrier_default()}
                                        </TableCell>
                                        <TableCell
                                            className={
                                                cfg.region
                                                    ? ''
                                                    : 'text-muted-foreground'
                                            }
                                        >
                                            {slaRegionLabel(cfg.region)}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id && editing ? (
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    className="w-24"
                                                    value={
                                                        editing.duration_minutes
                                                    }
                                                    onChange={(e) =>
                                                        setEditing((prev) =>
                                                            prev
                                                                ? {
                                                                      ...prev,
                                                                      duration_minutes:
                                                                          Number(
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                          ),
                                                                  }
                                                                : prev,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                cfg.duration_minutes
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id && editing ? (
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    className="w-24"
                                                    value={
                                                        editing.warning_minutes
                                                    }
                                                    placeholder="—"
                                                    onChange={(e) =>
                                                        setEditing((prev) =>
                                                            prev
                                                                ? {
                                                                      ...prev,
                                                                      warning_minutes:
                                                                          e
                                                                              .target
                                                                              .value
                                                                              ? Number(
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                              : '',
                                                                  }
                                                                : prev,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                (cfg.warning_minutes ?? '—')
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    activeMutation.mutate({
                                                        id: cfg.id,
                                                        is_active:
                                                            !cfg.is_active,
                                                    })
                                                }
                                                className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${
                                                    cfg.is_active
                                                        ? 'border-green-500 text-green-600 bg-green-50'
                                                        : 'border-muted text-muted-foreground bg-muted/30'
                                                }`}
                                            >
                                                {cfg.is_active
                                                    ? m.sla_active()
                                                    : '—'}
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id ? (
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        disabled={
                                                            upsertMutation.isPending
                                                        }
                                                        onClick={handleSave}
                                                    >
                                                        {m.common_save()}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setEditing(null)
                                                            setEditingId(null)
                                                        }}
                                                    >
                                                        {m.common_cancel()}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        startEdit(cfg)
                                                    }
                                                >
                                                    {m.common_edit()}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <SLAConfigAddDialog
                open={adding}
                onOpenChange={setAdding}
                onSubmit={handleAdd}
                submitting={upsertMutation.isPending}
            />
        </div>
    )
}
